import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { v4 as uuid } from 'uuid'
import { CatalogueEntry } from 'models/catalogues'
import { Concept, TaxCatalogue } from 'models/invoices.models'
import Product from 'models/product'
import { useThunkDispatch } from 'models/thunk'
import { selectErrorDialog } from 'lib/helpers/errors.helpers'
import {
  selectCatalogue,
  selectCreateCreditNote,
  selectSelectedInvoice,
} from 'lib/helpers/selectors'
import {
  downloadFileFromData,
  filterCatalogueRelationByType,
  validateCreditNoteComplete,
} from 'lib/helpers/utilities'
import useDialog from 'hooks/Shared/useDialog'
import useLoadingAdditionalInformation from 'hooks/Shared/useLoadingAdditionalInformation'
import { useAppSelector } from 'store/hooks'
import { getCategoriesList } from 'actions/categories'
import { resetForm, storeFormValue } from 'actions/forms'
import {
  createCreditNote,
  createCreditNoteConcept,
  getCreditNoteFiles,
  getIncomeInvoiceById,
  getTaxCatalogue,
} from 'actions/invoices'
import { setLocalLoader } from 'actions/loader'
import { getProductsList } from 'actions/products'
import { IAddCreditNoteContainerProps } from './AddCreditNoteContainer'

const useAddCreditNoteContainer = ({
  workspaceId,
  invoiceId,
  cleanup,
}: IAddCreditNoteContainerProps) => {
  const dispatch = useDispatch()
  const thunkDispatch = useThunkDispatch()

  const dialog = useDialog()

  const form = useAppSelector(selectCreateCreditNote)
  const invoice = useAppSelector(selectSelectedInvoice)
  const usosCfdi = useAppSelector(selectCatalogue('usosCfdi'))
  const metodosPago = useAppSelector(selectCatalogue('metodosPago'))
  const tipoRelacion = useAppSelector(selectCatalogue('tiposRelaciones'))
  const idInvoice = invoice?.idCfdi

  const [loading, setLoading] = useState(false)
  const [valid, setValid] = useState(false)
  const [formValid, setFormValid] = useState(false)
  const [taxes, setTaxes] = useState<TaxCatalogue>([])
  const [creditNoteSuccess, setcreditNoteSuccess] = useState(false)

  const showAdditionalInformationLoading = useLoadingAdditionalInformation()

  const filterTipoRelacion = useMemo(
    () => filterCatalogueRelationByType(invoice?.type.value, tipoRelacion),
    [invoice?.type.value, tipoRelacion]
  )

  const getInformation = async () => {
    dispatch(setLocalLoader(true, 'Cargando catálogos personalizados'))
    saveValueToStore('serie', invoice?.serie)
    saveValueToStore('folio', invoice?.folio)
    saveValueToStore('uuid', invoice?.uuid)
    try {
      const req = await Promise.all([
        thunkDispatch(getIncomeInvoiceById(workspaceId, invoiceId)),
        thunkDispatch(getTaxCatalogue()),
        thunkDispatch(getProductsList(workspaceId)),
        thunkDispatch(getCategoriesList(workspaceId)),
      ])
      setTaxes(req[1])
    } catch (err) {
      console.log(err)
      dialog.danger({
        subtitle:
          'No se pudo obtener la información de los catálogos. Intenta más tarde.',
        primaryButtonText: 'Reintentar',
        secondaryButtonText: 'Cancelar',
        onPrimaryClick: () => {
          dispatch(setLocalLoader(false))
          getInformation()
        },
        onSecondaryClick: () => {
          dispatch(setLocalLoader(false))
          cleanup()
        },
      })
    }
    finally {
      dispatch(setLocalLoader(false))
    }
  }

  const onDownloadCreditNoteFiles = useCallback(async () => {
    if (!idInvoice) return
    try {
      showAdditionalInformationLoading()
      const files = await thunkDispatch(
        getCreditNoteFiles(workspaceId, idInvoice, form.idCreditNote)
      )
      dialog.close()
      Object.entries(files).forEach(([key, value]) => {
        downloadFileFromData(`${form.uuid}.${key}`, value)
      })
      cleanup()
    } catch (err) {
      setTimeout(() => {
        dialog.danger({
          title: 'generic.error.title',
          subtitle:
            'No se pudo descargar la nota de crédito. Intenta nuevamente.',
        })
      }, 600)
    }
  }, [
    dialog,
    form,
    idInvoice,
    workspaceId,
    cleanup,
    showAdditionalInformationLoading,
    thunkDispatch,
  ])

  const onAdd = async (form: any) => {
    setLoading(true)
    try {
      let response = await thunkDispatch(createCreditNote(workspaceId, form))
      if (response) {
        dispatch(
          storeFormValue(
            'createCreditNote',
            'idCreditNote',
            response.idCreditNote
          )
        )
      }
      dispatch(resetForm('createPaymentComplement'))
      setcreditNoteSuccess(true)
    } catch (err: any) {
      const op = selectErrorDialog(err)
      if (op?.variant) {
        await dialog[op.variant](op)
        return
      }
      await dialog.dangerAsync({
        subtitle: 'No se pudo crear la nota de crédito. Intenta nuevamente.',
      })
    } finally {
      setLoading(false)
    }
  }

  const addConcept = (p?: Product) => {
    const { claveSat, claveUnitSat, description, objImp, price } = p ?? {}

    const concept: Concept = {
      cantidad: '1',
      claveUnitSat,
      description: description ?? '',
      descuento: '',
      importacion: '',
      impuestos: [],
      objImp,
      predial: '',
      price: price ? `${price} ` : '',
      satKey: claveSat,
      uuid: uuid(),
    }

    dispatch(createCreditNoteConcept(concept))
  }

  const saveValueToStore = useCallback(
    (name: string, value: any) => {
      dispatch(storeFormValue('createCreditNote', name, value))
    },
    [dispatch]
  )

  const onCancelCreditNote = useCallback(() => {
    dispatch(resetForm('createPaymentComplement'))
    cleanup()
  }, [dispatch, cleanup])

  const onSetExchangeRate = useCallback(
    (value: CatalogueEntry) => {
      saveValueToStore('moneda', value)
      if (value?.value === 'MXN') {
        saveValueToStore('exchangeRate', 0)
      }
    },
    [saveValueToStore]
  )

  useEffect(() => {
    getInformation()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, invoiceId])
  //cargar por defecto ciertos valores de campos
  useEffect(() => {
    if (!form.usoCfdi) {
      const usoCfdi = usosCfdi.filter(m => m.value === 'G02')[0]
      saveValueToStore('usoCfdi', usoCfdi)
    }
    if (!form.metodoPago) {
      const metodoPago = metodosPago.filter(m => m.value === 'PUE')[0]
      saveValueToStore('metodoPago', metodoPago)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  //Validar state de creditNote
  useEffect(() => {
    setFormValid(validateCreditNoteComplete(form))
  }, [form])

  return {
    creditNoteSuccess,
    form,
    formValid,
    invoice,
    loading,
    metodosPago,
    taxes,
    usosCfdi,
    filterTipoRelacion,
    valid,
    addConcept,
    onAdd,
    onCancelCreditNote,
    onDownloadCreditNoteFiles,
    onSetExchangeRate,
    saveValueToStore,
    setValid,
  }
}

export default useAddCreditNoteContainer
